// The analysis chain from orb/playground/index.html (v46), on PCM frames
// instead of a MediaStream: the Rust side already captures the microphone
// for the server, and the same frames come here. Everything Web Audio did
// in the playground is done by hand so the numbers match it:
//   highpass 150 Hz (biquad, Q 0.7) → AnalyserNode fftSize 256,
//   smoothing 0, Blackman window, bytes over −100…−30 dB
// then, verbatim from the playground: the voice band 200–4000 Hz, the
// per-bin noise ceiling (calibrated 0.8 s, then down 2 s / up 6 s, frozen
// at 30 s while voice is heard), the mean of the strongest quarter of bins
// above their ceilings, a 50/150 ms smoothing and the soft knee 0.03 → 0.18,
// and the log-spaced bar picks shaped by a running spectral peak.
//
// Instead of the playground's Silero VAD, which ran in the browser on
// the MediaStream and is not here: a gate of two cheap tests, spectral
// flatness and duration (2026-09-22). It is not a speech model and will
// not tell a voice from a violin, but it is what stands between the
// bars and every keyboard, door and fan in the room.

// What separates a voice from the room. FLATNESS is the spectral
// flatness a frame must be under to count as one: measured on this
// harness, speech sits around 0.1–0.3 and white noise at 0.7–1.0, so
// the line is drawn where nothing that is not speech reaches.
// ATTACK is how long it must last before the bars move, which is longer
// than a key press and shorter than a syllable; RELEASE holds them
// through the stops inside a word.
const FLATNESS = 0.45;
const ATTACK = 0.12;
const RELEASE = 0.25;

const FFT = 256;
const BINS = FFT / 2;
const MIN_DB = -100,
  MAX_DB = -30;

export type AnalysisConfig = { bars: number; react: number };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

// RBJ highpass, the formula Web Audio's BiquadFilterNode uses.
function highpass(fs: number, f0: number, Q: number) {
  const w0 = (2 * Math.PI * f0) / fs,
    cw = Math.cos(w0),
    alpha = Math.sin(w0) / (2 * Q);
  const a0 = 1 + alpha;
  const b0 = (1 + cw) / 2 / a0,
    b1 = -(1 + cw) / a0,
    b2 = (1 + cw) / 2 / a0;
  const a1 = (-2 * cw) / a0,
    a2 = (1 - alpha) / a0;
  let x1 = 0,
    x2 = 0,
    y1 = 0,
    y2 = 0;
  return (x: number) => {
    const y = b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1;
    x1 = x;
    y2 = y1;
    y1 = y;
    return y;
  };
}

// Blackman window as the Web Audio spec defines it for AnalyserNode.
const WINDOW = new Float32Array(FFT);
for (let n = 0; n < FFT; n++)
  WINDOW[n] =
    0.42 -
    0.5 * Math.cos((2 * Math.PI * n) / FFT) +
    0.08 * Math.cos((4 * Math.PI * n) / FFT);

// In-place radix-2 FFT of FFT points.
function fft(re: Float32Array, im: Float32Array) {
  for (let i = 1, j = 0; i < FFT; i++) {
    let bit = FFT >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }
  for (let len = 2; len <= FFT; len <<= 1) {
    const ang = (-2 * Math.PI) / len,
      wr = Math.cos(ang),
      wi = Math.sin(ang);
    for (let i = 0; i < FFT; i += len) {
      let cr = 1,
        ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k,
          b = a + len / 2;
        const tr = re[b] * cr - im[b] * ci,
          ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr;
        im[b] = im[a] - ti;
        re[a] += tr;
        im[a] += ti;
        const ncr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = ncr;
      }
    }
  }
}

export function createAnalysis(sampleRate: number, cfg: AnalysisConfig) {
  const hp = highpass(sampleRate, 150, 0.7);
  // the last FFT samples after the highpass, as the AnalyserNode keeps them
  const ring = new Float32Array(FFT);
  let head = 0;
  const re = new Float32Array(FFT),
    im = new Float32Array(FFT),
    fbuf = new Uint8Array(BINS);
  const spec = new Float32Array(256),
    binFloor = new Float32Array(512),
    excess = new Float32Array(512);
  let calib = 0,
    gateOpen = false,
    voiced = 0,
    held = 0,
    bandS = 0,
    specPeak = 0,
    dbg = "";
  const hz = sampleRate / FFT,
    lo = Math.floor(200 / hz),
    hi = Math.min(BINS - 1, Math.ceil(4000 / hz));

  function bytes() {
    for (let n = 0; n < FFT; n++) {
      re[n] = ring[(head + n) % FFT] * WINDOW[n];
      im[n] = 0;
    }
    fft(re, im);
    for (let k = 0; k < BINS; k++) {
      const mag = Math.hypot(re[k], im[k]) / FFT;
      const db = mag > 0 ? 20 * Math.log10(mag) : -Infinity;
      fbuf[k] = Math.max(
        0,
        Math.min(255, Math.round((255 / (MAX_DB - MIN_DB)) * (db - MIN_DB))),
      );
    }
  }

  return {
    /// Audio in: 16-bit or float PCM at the analysis's sample rate.
    push(pcm: Int16Array | Float32Array) {
      const scale = pcm instanceof Int16Array ? 1 / 0x8000 : 1;
      for (let i = 0; i < pcm.length; i++) {
        ring[head] = hp(pcm[i] * scale);
        head = (head + 1) % FFT;
      }
    },
    /// One animation frame: the instantaneous level 0..1 (the envelope is
    /// the renderer's) and the smoothed bar spectrum. Inactive frames
    /// (no source for this state) read as silence and let the bars decay.
    sample(dt: number, active: boolean) {
      let target = 0;
      const n = cfg.bars;
      if (active) {
        bytes();
        calib += dt;
        // per-bin noise floor over the whole voice band; calibrated in the first 0.8 s, then down fast / up slowly
        let cnt = 0;
        for (let b = lo; b <= hi; b++) {
          const raw = fbuf[b] / 255;
          // noise CEILING per bin: calibration takes the max seen; afterwards
          // it rises fast (0.5 s) and falls slowly (6 s), so it sits over the
          // noise's peaks instead of settling on its mean — a ceiling that
          // lags the peaks leaves every loud noise frame above it, and the
          // level below is the mean of the strongest quarter of bins, which
          // for noise is always well over its mean: the bars vibrated in a
          // quiet room (2026-09-21). Speech is what the freeze is for: once
          // the gate is open the ceiling barely moves (30 s), so a sustained
          // voice is never chased.
          if (calib < 0.05) binFloor[b] = raw;
          else if (calib < 0.8) binFloor[b] = Math.max(binFloor[b], raw);
          else
            binFloor[b] +=
              (raw - binFloor[b]) *
              Math.min(1, dt / (raw < binFloor[b] ? 6 : gateOpen ? 30 : 0.5));
          excess[b] = Math.max(0, raw - binFloor[b]);
          cnt++;
        }
        // level = mean of the strongest quarter of bins above their floors: a narrow vowel like "u" counts fully
        const top = Array.from(excess.subarray(lo, hi + 1)).sort(
          (a, b) => b - a,
        );
        const q = Math.max(1, Math.floor(top.length / 4));
        let sum = 0;
        for (let k = 0; k < q; k++) sum += top[k];
        const band = sum / q;
        // Is this a voice at all? Loudness alone says no: a keyboard, a
        // door and a fan all clear the ceiling, and the bars answered
        // every one of them (2026-09-22, Nuutti: "reagoi edelleen
        // kaikkiin taustaääniin"). Two things separate speech from the
        // room, and both are cheap.
        //
        // Shape: a voice is harmonics and formants, so its band is
        // peaky; a click, a clatter and a hiss spread their energy
        // evenly. Spectral flatness — the geometric mean over the
        // arithmetic mean — is near 1 for the flat ones and well under
        // it for a voice.
        // Measured on the power in each bin, not on the excess over the
        // ceiling: the excess is clamped at zero, and a band full of
        // zeros has a geometric mean of nothing, which reads as the
        // peakiest thing there is. Noise looked like a voice until this
        // was moved back to the spectrum itself.
        let logSum = 0,
          linSum = 0,
          bins = 0;
        for (let b = lo; b <= hi; b++) {
          // fbuf is decibels mapped onto 0..1; undo that for a power.
          const p = Math.pow(10, ((fbuf[b] / 255) * (MAX_DB - MIN_DB) + MIN_DB) / 10) + 1e-12;
          logSum += Math.log(p);
          linSum += p;
          bins++;
        }
        const flatness = bins > 0 ? Math.exp(logSum / bins) / (linSum / bins) : 1;
        const voiceLike = flatness < FLATNESS;

        // Time: a voice lasts. A key press is a few tens of
        // milliseconds, so nothing opens the gate before it has been
        // there for ATTACK, and once open it stays for RELEASE so the
        // gaps inside a word do not chop the bars.
        if (band > bandS) bandS += (band - bandS) * Math.min(1, dt / 0.05);
        else bandS += (band - bandS) * Math.min(1, dt / 0.15);
        const loud = bandS > 0.03;
        if (calib < 0.8) {
          gateOpen = false;
          voiced = 0;
          held = 0;
          target = 0;
          bandS = 0;
        } else {
          // The hold starts when the gate opens, not when a frame
          // happens to qualify: the leading edge of any loud sound is
          // peaky — a step is not flat — and setting the hold there let
          // one frame of a door slam light the bars for a quarter of a
          // second.
          if (loud && voiceLike) voiced += dt;
          else voiced = 0;
          const open = voiced >= ATTACK;
          held = open ? RELEASE : Math.max(0, held - dt);
          gateOpen = open || held > 0;
          // The knee is unchanged; the gate only decides whether it is
          // applied at all, so a voice still fades in and out smoothly.
          const x = Math.min(1, Math.max(0, (bandS - 0.03) / 0.15));
          target = gateOpen ? x * x * (3 - 2 * x) : 0;
        }
        dbg =
          "excess " +
          band.toFixed(2) +
          " flat " +
          flatness.toFixed(2) +
          (calib < 0.8 ? " · calibrating" : gateOpen ? " · voice" : " · quiet");
        void cnt;
      }
      // spectrum for the listening bars
      const rel = lerp(0.25, 0.06, cfg.react / 100);
      for (let i = 0; i < n; i++) {
        let v = 0;
        if (active) {
          const idx = lo + Math.floor(Math.pow(i / n, 1.5) * (hi - lo));
          // relative scaling: shape from excess / running spectral peak, overall length from loudness (target)
          const e = Math.max(0, excess[idx] - 0.02);
          if (i === 0) {
            const pk = Math.max(...Array.from(excess.subarray(lo, hi + 1)));
            if (pk > specPeak)
              specPeak += (pk - specPeak) * Math.min(1, dt / 0.05);
            else specPeak += (pk - specPeak) * Math.min(1, dt / 2);
          }
          const shape =
            specPeak > 0.03 ? Math.pow(Math.min(1, e / specPeak), 0.8) : 0;
          v = gateOpen ? shape * Math.min(1, target * 1.5) : 0; // no baseline: when the gate says silence, bars are zero
        }
        if (v > spec[i]) spec[i] = v;
        else spec[i] += (v - spec[i]) * Math.min(1, dt / (rel * 0.8));
      }
      return { level: target, spectrum: spec.subarray(0, n) };
    },
    get dbg() {
      return dbg;
    },
  };
}
